import { useEffect, useRef, useState } from 'react';
import { GridView } from '@vivli/shared/views';
import { ColDef, GridApi, ICellRendererParams, RowNode } from 'ag-grid-community';
import { IUploadActionDetailDto } from '@vivli/features/dashboard/infrastructure/interface';
import { multiTypeComparator, useIndividualAwaitingActionsHook } from '@vivli/features/dashboard/infrastructure/hook';
import { first } from 'rxjs/operators';
import { UploadActionCardWrapperComponent } from './upload-action-card-wrapper.component';
import { actionGridContainerStyle } from './actions-grids-styles';
import { useModalApiContext } from '@vivli/core/infrastructure/context';

interface UploadActionsGridComponentProps {
    onGridReady?: (gridApi: GridApi) => void;
    emptyMessage?: string;
    hideFloatingFilter?: boolean;
    hideHeader?: boolean;
    autoHeight?: boolean;
    asyncUpdateKey?: string;
    dtos?: IUploadActionDetailDto[];
    rowHeight?: number;
    synchUploadCount?: (number: number) => void;
}

export const UploadActionsGridComponent = ({
    onGridReady,
    asyncUpdateKey,
    dtos,
    rowHeight,
    synchUploadCount
}: UploadActionsGridComponentProps) => {
    const { getRequestsAndUploads$ } = useIndividualAwaitingActionsHook();
    const [rowData, setRowData] = useState<IUploadActionDetailDto[]>();
    const gridApiRef = useRef<GridApi>();
    const modal = useModalApiContext();

    const handleOnGridReady = (gridApi: GridApi) => {
        onGridReady && onGridReady(gridApi);
        gridApiRef.current = gridApi;
    };

    const handleRefreshedError = (error: any) => {
        try {
            const message = error?.message ?? 'Error fetching updated upload action details after an upload.';
            modal.error(message);
        } catch {}
    };

    const columnDefs: ColDef[] = [
        {
            field: 'studyId',
            headerName: 'Study ID',
            filter: true,
            sortable: true,
            valueGetter: (params) => params.data?.studyId ?? null,
            comparator: multiTypeComparator
        },
        {
            field: 'uniqueId',
            headerName: 'Sponsor ID',
            filter: true,
            sortable: true
        },
        {
            field: 'title',
            headerName: 'Study Title',
            filter: false,
            sortable: false,
            hide: true
        },
        {
            field: 'dataRequestId',
            headerName: 'Data Request ID',
            filter: true,
            sortable: true
        },
        {
            field: 'requestType',
            headerName: 'Request Type',
            filter: true,
            sortable: true,
            hide: true
        },
        {
            field: 'availability',
            headerName: 'Availability',
            filter: true,
            sortable: true,
            hide: true
        },
        {
            field: 'actionText',
            headerName: 'Action Required',
            filter: true,
            sortable: true,
            hide: false
        }
    ];

    const getFullWidthRenderer = (params: ICellRendererParams<IUploadActionDetailDto>) => {
        const { data, node, api } = params;
        const setRowHeight = (height: number) => {
            if (node && typeof (node as RowNode).setRowHeight === 'function') {
                (node as RowNode).setRowHeight(height);
            }
            const apiAny = api as unknown as { onRowHeightChanged?: () => void };
            if (apiAny.onRowHeightChanged) {
                apiAny.onRowHeightChanged();
            }
        };
        return (
            <UploadActionCardWrapperComponent
                dto={data}
                onHeightChange={setRowHeight}
                handleRefreshAfterUpload={handleRefreshAfterUpload}
            />
        );
    };

    //Grid needs new data because it did an upload,
    //get new data that will not include the upload we just did
    const handleRefreshAfterUpload = async () => {
        getRequestsAndUploads$()
            ?.pipe(first())
            .subscribe({
                next: (newRequestsAndUploads) => {
                    const uploadDtos = newRequestsAndUploads?.uploadActionDetails;
                    setRowData(uploadDtos);
                    synchUploadCount(uploadDtos?.length ?? 0);
                },
                error: handleRefreshedError
            });
    };

    useEffect(() => {
        if (!dtos || dtos.length === 0) {
            return;
        }
        setRowData(dtos);
    }, [dtos]);

    useEffect(() => {
        if (!gridApiRef.current) return;
        const apiAny = gridApiRef.current as unknown as { resetRowHeights?: () => void; onRowHeightChanged?: () => void };
        if (apiAny.resetRowHeights) {
            apiAny.resetRowHeights();
        } else if (apiAny.onRowHeightChanged) {
            apiAny.onRowHeightChanged();
        }
    }, [rowHeight]);

    return (
        <div style={actionGridContainerStyle}>
            <GridView
                rowData={rowData}
                useFullWidthRows={true}
                onGridReady={handleOnGridReady}
                fullWidthCellRenderer={getFullWidthRenderer}
                columns={columnDefs}
                rowHeight={rowHeight}
            />
        </div>
    );
};
